/**
 * Finance money normalization — the single defence against the intentional
 * two-shape money contract (see `docs/api-requirements.md` API-FIN-8 gotcha):
 *
 *  - `pending-balances.totalPendingCents` is a **string of minor units**
 *    (`"125000"` = $1,250.00), BigInt-safe on the wire.
 *  - owner-payout `amount` / projection amounts are **numbers in major units**
 *    (`1250.00` dollars, `1200` yen).
 *
 * Both funnel through a named constructor into one internal `Money`
 * (integer minor units + currency), so no component ever reasons about
 * cents-vs-dollars. `amountMinor` is `NaN` when the raw value can't be
 * parsed — `formatMoney` renders that as an em-dash rather than crashing.
 *
 * Side-effect free — unit-tested in `money.test.ts`.
 */
import { formatMinorUnits, majorNumberToMinorUnits } from "@/shared/lib/money";

export interface Money {
  /** Integer smallest-currency-unit amount; `NaN` when unparseable. */
  readonly amountMinor: number;
  readonly currency: string;
}

/** Strict integer, optionally signed. Rejects decimals, blanks, and junk. */
const INTEGER_PATTERN = /^-?\d+$/;

/**
 * API-FIN-1 shape: a string of minor units. Also accepts a numeric input
 * defensively — the pay-response `results[].amountCents` is now schema-confirmed
 * to be a `string` (`PayVenueCurrencyResultModel`, 2026-07-01), but the parameter
 * stays `string | number` so this helper can't be broken by a future caller
 * passing a numeric minor-units value. `String(raw)` normalizes both.
 *
 * `totalPendingCents` is a BigInt-safe string on the wire. Parsing with
 * `Number()` is exact for any realistic venue balance, but would silently
 * lose precision past `MAX_SAFE_INTEGER` — so an out-of-range/anomalous value
 * degrades to `NaN` (rendered as an em-dash) rather than a wrong number.
 */
export function moneyFromCentsString(raw: string | number, currency: string): Money {
  const text = String(raw).trim();
  const parsed = INTEGER_PATTERN.test(text) ? Number(text) : Number.NaN;
  const amountMinor = Number.isSafeInteger(parsed) ? parsed : Number.NaN;
  return { amountMinor, currency };
}

/** API-FIN-8 shape: a number in major units (dollars / yen). */
export function moneyFromMajorNumber(amount: number, currency: string): Money {
  const minor = majorNumberToMinorUnits(amount, currency);
  return { amountMinor: minor ?? Number.NaN, currency };
}

export function formatMoney(money: Money): string {
  const amount = Number.isFinite(money.amountMinor) ? money.amountMinor : null;
  return formatMinorUnits(amount, money.currency);
}
